import React, { useEffect, useState, useCallback, useRef } from 'react';
import { axiosInstance } from '../../utils/axios';
import PageHeader from './PageHeader';
import ChildProfileModal from './ChildProfileModal';
import ChildMedicalAidModal from './ChildMedicalAidModal';
import AlertModal from '../common/AlertModal';
import { convertHeicToJpeg, isHeicFile } from '../../utils/imageConverter';
import './medichat_mypage.scss';

function calcAge(birthDateStr) {
  if (!birthDateStr) return null;
  const today = new Date();
  const birth = new Date(birthDateStr);
  if (isNaN(birth.getTime())) return null;
  let age = today.getFullYear() - birth.getFullYear();
  if (today.getMonth() - birth.getMonth() < 0 ||
    (today.getMonth() - birth.getMonth() === 0 && today.getDate() < birth.getDate())) age -= 1;
  return age;
}
function genderLabel(g) {
  return g === 'male' ? '男の子' : g === 'female' ? '女の子' : g === 'other' ? 'その他' : '';
}
function formatDate(d) {
  if (!d) return '';
  const [y, m, day] = d.split('-');
  return `${y}年${parseInt(m)}月${parseInt(day)}日`;
}

const ALLERGY_LABELS = {
  allergy_wheat: '小麦', allergy_egg: '卵', allergy_milk: '乳',
  allergy_shrimp: 'えび', allergy_crab: 'かに', allergy_buckwheat: 'そば', allergy_peanut: '落花生',
};
const HISTORY_LABELS = {
  history_pneumonia: '肺炎', history_heart_disease: '心臓病', history_convulsion: 'けいれん',
  history_atopic_dermatitis: 'アトピー', history_congenital_disease: '先天性疾患',
  history_urinary_tract_infection: '尿路感染症', history_kawasaki_disease: '川崎病',
  history_developmental_disorder: '発達障害', history_asthma: '喘息',
  history_hospitalization_surgery: '入院・手術',
};

const AVATAR_COLORS = [
  'linear-gradient(135deg,#667eea,#764ba2)',
  'linear-gradient(135deg,#f093fb,#f5576c)',
  'linear-gradient(135deg,#4facfe,#00f2fe)',
  'linear-gradient(135deg,#43e97b,#38f9d7)',
  'linear-gradient(135deg,#fa709a,#fee140)',
];

function getAllergies(p) {
  const items = Object.entries(ALLERGY_LABELS).filter(([k]) => p[k]).map(([, v]) => v);
  if (p.allergy_other) items.push(p.allergy_other);
  return items;
}
function getHistories(p) {
  const items = Object.entries(HISTORY_LABELS).filter(([k]) => p[k]).map(([, v]) => v);
  if (p.history_other) items.push(p.history_other);
  return items;
}

const SectionLabel = ({ icon, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: '600', color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>
    <span>{icon}</span><span>{label}</span>
  </div>
);

const TagList = ({ items, color = '#4f46e5', bg = '#eef2ff', emptyText = 'なし', emptyColor = '#94a3b8' }) => {
  if (!items || items.length === 0) {
    return <span style={{ fontSize: '14px', color: emptyColor }}>{emptyText}</span>;
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
      {items.map((item, i) => (
        <span key={i} style={{ fontSize: '12px', fontWeight: '500', color, background: bg, padding: '3px 10px', borderRadius: '99px' }}>{item}</span>
      ))}
    </div>
  );
};

const InlineMedicalAidUploader = ({ profile, onUploaded, onError }) => {
  const frontRef = useRef(null);
  const backRef = useRef(null);
  const [front, setFront] = useState(null);
  const [back, setBack] = useState(null);
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    e?.stopPropagation?.();
    if (!front || busy) return;

    setBusy(true);
    try {
      let uploadFront = front;
      let uploadBack = back;
      if (isHeicFile(uploadFront)) uploadFront = await convertHeicToJpeg(uploadFront);
      if (uploadBack && isHeicFile(uploadBack)) uploadBack = await convertHeicToJpeg(uploadBack);

      const fd = new FormData();
      fd.append('front', uploadFront);
      if (uploadBack) fd.append('back', uploadBack);

      await axiosInstance.post(
        `/api/medichat/child_profiles/${profile.id}/child_medical_aids/upload`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setFront(null);
      setBack(null);
      await onUploaded?.();
    } catch (err) {
      onError?.(err?.response?.data?.error || '医療証のアップロードに失敗しました');
    } finally {
      setBusy(false);
    }
  };

  const pickerStyle = (hasFile) => ({
    flex: 1,
    minHeight: '88px',
    border: `1.5px dashed ${hasFile ? '#4f46e5' : '#d1d5db'}`,
    borderRadius: '12px',
    background: hasFile ? '#eef2ff' : '#fafafa',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '10px',
    cursor: 'pointer',
    textAlign: 'center',
  });

  return (
    <div style={{ display: 'grid', gap: '10px' }}>
      <div style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.5' }}>
        子ども医療証はこの場でアップロードできます。表面のみ必須です。
      </div>
      <div style={{ display: 'flex', gap: '10px' }}>
        <input
          ref={frontRef}
          type="file"
          accept="image/*,.heic,.heif"
          style={{ display: 'none' }}
          onChange={(e) => setFront(e.target.files?.[0] || null)}
        />
        <button type="button" onClick={(e) => { e.stopPropagation(); frontRef.current?.click(); }} style={pickerStyle(!!front)}>
          <div style={{ fontSize: '20px', marginBottom: '6px' }}>📷</div>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>医療証（表）</div>
          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>{front ? front.name : '選択する'}</div>
        </button>

        <input
          ref={backRef}
          type="file"
          accept="image/*,.heic,.heif"
          style={{ display: 'none' }}
          onChange={(e) => setBack(e.target.files?.[0] || null)}
        />
        <button type="button" onClick={(e) => { e.stopPropagation(); backRef.current?.click(); }} style={pickerStyle(!!back)}>
          <div style={{ fontSize: '20px', marginBottom: '6px' }}>🪪</div>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>医療証（裏）</div>
          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>{back ? back.name : '任意'}</div>
        </button>
      </div>
      <button
        type="button"
        onClick={upload}
        disabled={!front || busy}
        style={{
          padding: '10px 14px',
          border: 'none',
          borderRadius: '10px',
          background: !front || busy ? '#e5e7eb' : 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)',
          color: !front || busy ? '#9ca3af' : '#fff',
          fontSize: '14px',
          fontWeight: '600',
          cursor: !front || busy ? 'not-allowed' : 'pointer',
        }}
      >
        {busy ? 'アップロード中…' : '医療証を登録'}
      </button>
    </div>
  );
};

const ChildProfilesPage = () => {
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [alertModal, setAlertModal] = useState({ open: false, title: '', message: '' });
  const [expandedId, setExpandedId] = useState(null);

  const [aidViewProfile, setAidViewProfile] = useState(null);
  const [aids, setAids] = useState([]);
  const [aidsLoading, setAidsLoading] = useState(false);
  const [aidModalOpen, setAidModalOpen] = useState(false);
  const [editingAid, setEditingAid] = useState(null);
  const [deleteAidTarget, setDeleteAidTarget] = useState(null);
  const [deletingAid, setDeletingAid] = useState(false);

  const load = async () => {
    const r = await axiosInstance.get('/api/medichat/child_profiles').catch(() => null);
    setList(r?.data?.child_profiles || r?.data || []);
  };
  useEffect(() => { load(); }, []);

  const setDefault = async (id) => {
    setBusy(true);
    const r = await axiosInstance.post(`/api/medichat/child_profiles/${id}/set_default`).catch(e => e?.response || null);
    setBusy(false);
    if (r?.status >= 200 && r?.status < 300) { await load(); }
    else { setAlertModal({ open: true, title: 'エラー', message: r?.data?.message || '設定に失敗しました' }); }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const r = await axiosInstance.delete(`/api/medichat/child_profiles/${deleteTarget.id}`).catch(e => e?.response || null);
    setDeleting(false);
    if (r?.status >= 200 && r?.status < 300) { setDeleteTarget(null); await load(); }
    else { setAlertModal({ open: true, title: 'エラー', message: r?.data?.message || '削除に失敗しました' }); }
  };

  const loadAids = useCallback(async (profileId) => {
    setAidsLoading(true);
    const r = await axiosInstance.get(`/api/medichat/child_profiles/${profileId}/child_medical_aids`).catch(() => null);
    setAids(r?.data?.child_medical_aids || []);
    setAidsLoading(false);
  }, []);

  const openAidView = (profile) => {
    setAidViewProfile(profile);
    loadAids(profile.id);
  };

  const doDeleteAid = async () => {
    if (!deleteAidTarget || !aidViewProfile) return;
    setDeletingAid(true);
    const r = await axiosInstance
      .delete(`/api/medichat/child_profiles/${aidViewProfile.id}/child_medical_aids/${deleteAidTarget.id}`)
      .catch(e => e?.response || null);
    setDeletingAid(false);
    if (r?.status >= 200 && r?.status < 300) {
      setDeleteAidTarget(null);
      loadAids(aidViewProfile.id);
    } else {
      setAlertModal({ open: true, title: 'エラー', message: r?.data?.message || '削除に失敗しました' });
    }
  };

  // === 医療証一覧画面 ===
  if (aidViewProfile) {
    return (
      <div className="mypage-container">
        <PageHeader
          title="医療証"
          onBack={() => setAidViewProfile(null)}
          right={
            <button onClick={() => { setEditingAid(null); setAidModalOpen(true); }} style={{ background: 'none', border: 'none', fontSize: '14px', color: '#6366f1', cursor: 'pointer', padding: '8px 0', fontFamily: 'inherit', fontWeight: '600' }}>追加</button>
          }
        />

        <div style={{ padding: '20px 16px 40px' }}>
          <div style={{ padding: '12px 14px', background: '#fff', borderRadius: '12px', marginBottom: '16px', boxShadow: '0 1px 0 rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize: '15px', fontWeight: '600', color: '#000' }}>{aidViewProfile.last_name} {aidViewProfile.first_name}</div>
            <div style={{ fontSize: '13px', color: '#8e8e93', marginTop: '2px' }}>子ども医療費助成証の管理</div>
          </div>

          {aidsLoading ? (
            <div style={{ textAlign: 'center', color: '#8e8e93', padding: '40px 0', fontSize: '15px' }}>読み込み中…</div>
          ) : aids.length === 0 ? (
            <div style={{ textAlign: 'center', paddingTop: '40px' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>🏥</div>
              <div style={{ fontSize: '17px', fontWeight: '600', color: '#000', marginBottom: '6px' }}>医療証が未登録です</div>
              <div style={{ fontSize: '14px', color: '#8e8e93', marginBottom: '24px' }}>子ども医療費助成証を登録すると、診察時に自動で適用されます</div>
              <button onClick={() => { setEditingAid(null); setAidModalOpen(true); }} style={{ background: '#007AFF', color: '#fff', border: 'none', borderRadius: '14px', padding: '14px 28px', fontSize: '17px', fontWeight: '600', cursor: 'pointer' }}>医療証を追加</button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {aids.map((aid) => {
                const isExpired = aid.valid_to && new Date(aid.valid_to) < new Date();
                return (
                  <div key={aid.id} style={{ background: '#fff', borderRadius: '12px', padding: '14px 16px', boxShadow: '0 1px 0 rgba(0,0,0,0.06)', opacity: isExpired ? 0.6 : 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontSize: '14px', fontWeight: '600', color: '#000' }}>{aid.kohi_type || '子ども医療費助成'}</span>
                      <span style={{ fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '9999px', background: isExpired ? '#fef2f2' : (aid.status === 'verified' ? '#ecfdf5' : '#fffbeb'), color: isExpired ? '#dc2626' : (aid.status === 'verified' ? '#059669' : '#d97706') }}>
                        {isExpired ? '期限切れ' : (aid.status === 'verified' ? '確認済' : '審査中')}
                      </span>
                    </div>
                    <div style={{ fontSize: '13px', color: '#6b7280', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                      <div>負担者番号: {aid.insurer_number}</div>
                      <div>受給者番号: {aid.recipient_number}</div>
                      <div>有効期間: {formatDate(aid.valid_from)} 〜 {aid.valid_to ? formatDate(aid.valid_to) : '無期限'}</div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                      <button onClick={() => { setEditingAid(aid); setAidModalOpen(true); }} style={{ flex: 1, padding: '8px', border: '1px solid #e5e7eb', borderRadius: '8px', background: '#fff', color: '#374151', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>編集</button>
                      <button onClick={() => setDeleteAidTarget(aid)} style={{ padding: '8px 16px', border: '1px solid #fecaca', borderRadius: '8px', background: '#fff', color: '#ef4444', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>削除</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {deleteAidTarget && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
            <div style={{ background: 'rgba(255,255,255,0.97)', borderRadius: '14px', overflow: 'hidden', width: '100%', maxWidth: '280px', backdropFilter: 'blur(40px)', textAlign: 'center' }}>
              <div style={{ padding: '20px 16px 0' }}>
                <div style={{ fontSize: '17px', fontWeight: '600', color: '#000', marginBottom: '8px' }}>医療証を削除</div>
                <div style={{ fontSize: '13px', color: '#8e8e93', marginBottom: '16px', lineHeight: '1.5' }}>この医療証を削除しますか？</div>
              </div>
              <div style={{ borderTop: '0.5px solid rgba(0,0,0,0.15)', display: 'flex' }}>
                <button onClick={() => setDeleteAidTarget(null)} disabled={deletingAid} style={{ flex: 1, padding: '14px', background: 'none', border: 'none', borderRight: '0.5px solid rgba(0,0,0,0.15)', fontSize: '17px', color: '#007AFF', cursor: 'pointer' }}>キャンセル</button>
                <button onClick={doDeleteAid} disabled={deletingAid} style={{ flex: 1, padding: '14px', background: 'none', border: 'none', fontSize: '17px', fontWeight: '600', color: '#ff3b30', cursor: 'pointer' }}>{deletingAid ? '削除中…' : '削除'}</button>
              </div>
            </div>
          </div>
        )}

        <ChildMedicalAidModal open={aidModalOpen} onClose={() => setAidModalOpen(false)} childProfile={aidViewProfile} editingAid={editingAid} onSaved={() => loadAids(aidViewProfile.id)} />
        <AlertModal open={alertModal.open} title={alertModal.title} message={alertModal.message} onClose={() => setAlertModal({ open: false, title: '', message: '' })} />
      </div>
    );
  }

  // === メイン画面 ===
  return (
    <div className="mypage-container">
      <PageHeader
        title="お子さま"
        right={
          <button onClick={() => { setEditing(null); setModalOpen(true); }} style={{ background: 'none', border: 'none', fontSize: '14px', color: '#6366f1', cursor: 'pointer', padding: '8px 0', fontFamily: 'inherit', fontWeight: '600' }}>追加</button>
        }
      />

      <div style={{ padding: '20px 16px 40px' }}>
        {!list ? (
          <div style={{ textAlign: 'center', color: '#8e8e93', padding: '60px 0', fontSize: '15px' }}>読み込み中…</div>
        ) : list.length === 0 ? (
          <div style={{ textAlign: 'center', paddingTop: '80px' }}>
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>👶</div>
            <div style={{ fontSize: '20px', fontWeight: '600', color: '#000', marginBottom: '8px' }}>お子さまが未登録です</div>
            <div style={{ fontSize: '15px', color: '#8e8e93', marginBottom: '28px' }}>お子さまのプロフィールを追加してください</div>
            <button onClick={() => { setEditing(null); setModalOpen(true); }} style={{ background: '#007AFF', color: '#fff', border: 'none', borderRadius: '14px', padding: '14px 32px', fontSize: '17px', fontWeight: '600', cursor: 'pointer' }}>お子さまを追加</button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {list.map((profile, idx) => {
              const age = calcAge(profile.birth_date);
              const grad = AVATAR_COLORS[idx % AVATAR_COLORS.length];
              const initial = (profile.last_name || '?')[0];
              const allergies = getAllergies(profile);
              const histories = getHistories(profile);
              const isExpanded = expandedId === profile.id;

              return (
                <div key={profile.id} style={{
                  background: '#fff', borderRadius: '16px', overflow: 'hidden',
                  boxShadow: profile.is_default
                    ? '0 0 0 2px #4f46e5, 0 2px 12px rgba(79,70,229,0.12)'
                    : '0 1px 4px rgba(0,0,0,0.06)',
                }}>
                  {/* ヘッダー部分（タップで展開/折りたたみ） */}
                  <div
                    onClick={() => setExpandedId(isExpanded ? null : profile.id)}
                    style={{ padding: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '14px', userSelect: 'none' }}
                  >
                    <div style={{
                      width: '50px', height: '50px', borderRadius: '50%', background: grad, flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '22px', fontWeight: '700', color: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    }}>{initial}</div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <span style={{ fontSize: '17px', fontWeight: '700', color: '#0f172a' }}>{profile.last_name} {profile.first_name}</span>
                        {profile.is_default && (
                          <span style={{ fontSize: '10px', fontWeight: '700', color: '#4f46e5', background: '#eef2ff', padding: '2px 7px', borderRadius: '99px' }}>デフォルト</span>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '8px', fontSize: '13px', color: '#64748b', flexWrap: 'wrap' }}>
                        {age !== null && <span>{age}歳</span>}
                        {profile.gender && <span>{genderLabel(profile.gender)}</span>}
                        {profile.birth_date && <span>{formatDate(profile.birth_date)}</span>}
                      </div>
                    </div>

                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0, transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                      <path d="M2 4l4 4 4-4" stroke="#C7C7CC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>

                  {/* 展開時の詳細情報 */}
                  {isExpanded && (
                    <div style={{ borderTop: '1px solid #f0f0f5' }}>
                      {/* アレルギー */}
                      <div style={{ padding: '14px 16px', borderBottom: '1px solid #f0f0f5' }}>
                        <SectionLabel icon="⚠️" label="アレルギー" />
                        {profile.has_allergy ? (
                          <TagList items={allergies} color="#dc2626" bg="#fef2f2" emptyText="詳細未登録" />
                        ) : (
                          <span style={{ fontSize: '14px', color: '#22c55e' }}>なし</span>
                        )}
                      </div>

                      {/* 既往歴 */}
                      <div style={{ padding: '14px 16px', borderBottom: '1px solid #f0f0f5' }}>
                        <SectionLabel icon="📋" label="既往歴・持病" />
                        {profile.has_medical_history ? (
                          <TagList items={histories} color="#0369a1" bg="#f0f9ff" emptyText="詳細未登録" />
                        ) : (
                          <span style={{ fontSize: '14px', color: '#22c55e' }}>なし</span>
                        )}
                      </div>

                      {/* 医療証 */}
                      <div style={{ padding: '14px 16px', borderBottom: '1px solid #f0f0f5' }}>
                        <SectionLabel icon="🏥" label="子ども医療証" />
                        {profile.has_medical_aid ? (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{
                                fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '9999px',
                                background: profile.medical_aid_status === 'verified' ? '#ecfdf5' : '#fffbeb',
                                color: profile.medical_aid_status === 'verified' ? '#059669' : '#d97706',
                              }}>
                                {profile.medical_aid_status === 'verified' ? '✓ 確認済' : '審査中'}
                              </span>
                              <span style={{ fontSize: '13px', color: '#374151' }}>{profile.medical_aid_kohi_type || '子ども医療費助成'}</span>
                            </div>
                            <button
                              onClick={(e) => { e.stopPropagation(); openAidView(profile); }}
                              style={{ background: 'none', border: 'none', fontSize: '13px', color: '#007AFF', cursor: 'pointer', padding: '4px 0' }}
                            >管理 →</button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span style={{ fontSize: '14px', color: '#f59e0b' }}>未登録</span>
                            <div style={{ flex: 1, marginLeft: '12px' }}>
                              <InlineMedicalAidUploader
                                profile={profile}
                                onUploaded={load}
                                onError={(message) => setAlertModal({ open: true, title: 'エラー', message })}
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* アクションボタン */}
                      <div style={{ padding: '12px 16px', display: 'flex', gap: '8px' }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); setEditing(profile); setModalOpen(true); }}
                          style={{
                            flex: 1, padding: '10px', border: '1.5px solid #e2e5f0', borderRadius: '10px',
                            background: '#fff', color: '#374151', fontSize: '14px', fontWeight: '600', cursor: 'pointer',
                          }}
                        >編集</button>
                        {!profile.is_default && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setDefault(profile.id); }}
                            disabled={busy}
                            style={{
                              flex: 1, padding: '10px', border: '1.5px solid #c7d2fe', borderRadius: '10px',
                              background: '#eef2ff', color: '#4f46e5', fontSize: '14px', fontWeight: '600', cursor: 'pointer',
                            }}
                          >デフォルトに設定</button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); setDeleteTarget(profile); }}
                          style={{
                            padding: '10px 14px', border: '1.5px solid #fecaca', borderRadius: '10px',
                            background: '#fff', color: '#ef4444', fontSize: '14px', fontWeight: '600', cursor: 'pointer',
                          }}
                        >削除</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 削除確認 */}
      {deleteTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: 'rgba(255,255,255,0.97)', borderRadius: '14px', overflow: 'hidden', width: '100%', maxWidth: '280px', backdropFilter: 'blur(40px)', textAlign: 'center' }}>
            <div style={{ padding: '20px 16px 0' }}>
              <div style={{ fontSize: '17px', fontWeight: '600', color: '#000', marginBottom: '8px' }}>{deleteTarget.last_name} {deleteTarget.first_name}を削除</div>
              <div style={{ fontSize: '13px', color: '#8e8e93', marginBottom: '16px', lineHeight: '1.5' }}>この操作は取り消せません。</div>
            </div>
            <div style={{ borderTop: '0.5px solid rgba(0,0,0,0.15)', display: 'flex' }}>
              <button onClick={() => setDeleteTarget(null)} disabled={deleting} style={{ flex: 1, padding: '14px', background: 'none', border: 'none', borderRight: '0.5px solid rgba(0,0,0,0.15)', fontSize: '17px', color: '#007AFF', cursor: 'pointer' }}>キャンセル</button>
              <button onClick={doDelete} disabled={deleting} style={{ flex: 1, padding: '14px', background: 'none', border: 'none', fontSize: '17px', fontWeight: '600', color: '#ff3b30', cursor: 'pointer' }}>{deleting ? '削除中…' : '削除'}</button>
            </div>
          </div>
        </div>
      )}

      <ChildProfileModal open={modalOpen} onClose={() => setModalOpen(false)} onSaved={load} initialProfile={editing} />
      <AlertModal open={alertModal.open} title={alertModal.title} message={alertModal.message} onClose={() => setAlertModal({ open: false, title: '', message: '' })} />
    </div>
  );
};

export default ChildProfilesPage;
